import {useMemo} from 'react'
import {useTranslation} from 'i18n'
import {GiftshopPackageActivesResponse, GiftshopPackageData} from 'types'
import {calcTimeDiffWithDays} from 'utils/dates'

export function usePackageMetrics(
  packageData: GiftshopPackageActivesResponse | GiftshopPackageData | undefined,
) {
  const {translate} = useTranslation()

  const timeMetrics = useMemo(() => {
    if (!packageData || !packageData?.expired_at) {
      return {
        hmsBreakdown: {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        },
        packageIsActive: false,
      }
    }

    const now = new Date()
    const hmsBreakdown = calcTimeDiffWithDays(now, packageData.expired_at)

    const {days, hours, minutes, seconds} = hmsBreakdown
    return {
      hmsBreakdown,
      packageIsActive:
        days >= 0 &&
        hours >= 0 &&
        minutes >= 0 &&
        seconds >= 0 &&
        days + hours + minutes + seconds > 0,
    }
  }, [packageData])

  const {hmsBreakdown, packageIsActive} = timeMetrics

  const timeLeft = useMemo(() => {
    if (!packageIsActive) return null

    const {days, hours, minutes} = hmsBreakdown

    const dayPhrase =
      days > 0
        ? `${days} ${translate('global:day', {
            count: days,
          }).toLowerCase()}`
        : null

    const hourPhrase =
      hours > 0
        ? `${hours} ${translate('global:hour', {
            count: hours,
          }).toLowerCase()}`
        : null

    const minutePhrase =
      minutes > 0
        ? `${minutes} ${translate('global:minute', {
            count: minutes,
          }).toLowerCase()}`
        : null

    if (days > 0) {
      return [dayPhrase, hourPhrase].filter((phrase) => phrase).join(' ')
    }

    if (hours > 0) {
      return [hourPhrase, minutePhrase].filter((phrase) => phrase).join(' ')
    }

    if (minutes >= 1) {
      return minutePhrase
    }

    return translate(
      'giftShop:packageDetailActiveTimeLeftLessThanAMin',
    ).toLowerCase()
  }, [hmsBreakdown, packageIsActive, translate])

  return useMemo(
    () => ({
      packageIsActive,
      timeLeft,
    }),
    [timeLeft, packageIsActive],
  )
}
